import type { SerializerOptions } from '@ember-data/legacy-compat';
import type { Snapshot } from '@ember-data/legacy-compat/-private';
import type { ModelSchema } from '@ember-data/store/types';

import type { RecordAttributes, RecordData } from 'webapp-types';
import { omit } from 'webapp/utils/common';

import ApplicationSerializer from 'webapp/serializers/application';

import type SpaceRecord from 'webapp/models/space';

export default class SpaceSerializer extends ApplicationSerializer {
	override getReadOnlyFields(_json: Record<string, unknown>): RecordData.Space.ReadOnlyKey[] {
		return [
			'activeUntil',
			'createdByName',
			'datasources',
			'isEnterprise',
			'sourceCount',
			'userCount',
			'boardCount',
			'customMetricCount',
			'stepsCompleted',
			'accountType',
		];
	}

	// json is Raw when it comes from BE, Serialized when BufferedModel.toJSON() runs a record through normalize
	override normalize(
		typeClass: ModelSchema,
		json: RecordData.Space.Raw | RecordData.Space.Serialized,
		prop: string | undefined,
	) {
		const { settings, ...data } = json;
		const attributes: Partial<RecordAttributes<SpaceRecord>> & { id?: string | number } = omit(
			data as Omit<RecordData.Space.Raw, 'settings'>,
			'accountProgress',
		);

		if (settings) {
			attributes.tags = settings.tags || [];
		}

		if ('accountProgress' in data && data.accountProgress?.stepsCompleted) {
			attributes.stepsCompleted = data.accountProgress.stepsCompleted;
		}

		return super.normalize(typeClass, attributes, prop);
	}

	override serialize(
		snapshot: Snapshot,
		options: SerializerOptions,
	): RecordData.Space.Serialized {
		// read-only fields are already removed by ApplicationSerializer
		const attributes = super.serialize(snapshot, options) as Omit<
			RecordAttributes<SpaceRecord>,
			RecordData.Space.ReadOnlyKey
		>;

		return {
			...omit(attributes, 'tags'),
			settings: { tags: attributes.tags || [] },
		};
	}
}
